import React, { useMemo, useState } from 'react';

// ── Types (mirror the mixing-batch payload + 2a-3 contract) ──────────────────
export interface DispositionAllocation {
  id: number;
  order_number: string;
  order_id: number;
  order_item_id: number;
  customer_name: string;
  allocated_weight: number;        // what THIS batch contributes to the order line
  order_item_total_lbs: number;    // the whole order line target
  already_filled_lbs: number;      // realized from OTHER (completed) batches
}
export interface MatchingProduct { id: number; name: string; sku: string; size_lbs: number; form: string; }
export interface MatchingBin {
  id: number; name: string; product_id: number | null; capacity_lbs: number;
  current_lbs: number; available_capacity_lbs: number; percentage_full: number;
}
export interface MatchingFeedBank { id: number; feed_form: string; quantity_lbs: number; }

type ExcessType = 'FEED_BANK' | 'BAGGER' | 'BYOC_BIN';
interface ExcessLeg { type: ExcessType; lbs: number; store_product_id?: number; bin_id?: number; }
interface MakeUp { order_item_id: number; lbs: number; source_type: 'BYOC_BIN' | 'FEED_BANK'; bin_id?: number; feed_bank_id?: number; }

export interface DispositionPayload {
  notes: string;
  excess_distribution: ExcessLeg[];
  close_order_items: number[];
  make_up: MakeUp[];
}

interface Props {
  batchNumber: string;
  formulaName: string;
  totalMilled: number;            // batch.total_added (what was actually milled)
  allocations: DispositionAllocation[];
  products: MatchingProduct[];
  bins: MatchingBin[];
  feedBank: MatchingFeedBank[];
  saving: boolean;
  onCancel: () => void;
  onSubmit: (payload: DispositionPayload) => void;
}

// Matches the backend gate (disposition_service.DISPOSITION_BALANCE_TOLERANCE_LBS).
const TOL = 2;
const round = (n: number) => Math.round(n * 100) / 100;

const BatchDispositionModal: React.FC<Props> = ({
  batchNumber, formulaName, totalMilled, allocations, products, bins, feedBank,
  saving, onCancel, onSubmit,
}) => {
  const orderPortion = useMemo(
    () => round(allocations.reduce((s, a) => s + a.allocated_weight, 0)),
    [allocations]);
  const excessTotal = round(totalMilled - orderPortion);

  const [excessLegs, setExcessLegs] = useState<ExcessLeg[]>(
    () => excessTotal > 0 ? [{ type: 'FEED_BANK', lbs: Math.round(excessTotal) }] : []);
  const [closeIds, setCloseIds] = useState<Set<number>>(new Set());
  const [makeUps, setMakeUps] = useState<Record<number, MakeUp>>({});
  const [notes, setNotes] = useState('');

  // ── Excess-leg balanced contribution (bagged legs count whole bags only) ───
  const legBalanced = (leg: ExcessLeg): { balanced: number; bagCount?: number; remainder?: number } => {
    if (leg.type === 'BAGGER' && leg.store_product_id) {
      const p = products.find(p => p.id === leg.store_product_id);
      if (p && p.size_lbs > 0) {
        const bagCount = Math.floor((leg.lbs || 0) / p.size_lbs);
        const balanced = round(bagCount * p.size_lbs);
        return { balanced, bagCount, remainder: round((leg.lbs || 0) - balanced) };
      }
    }
    return { balanced: round(leg.lbs || 0) };
  };

  const placedBalanced = useMemo(
    () => round(excessLegs.reduce((s, l) => s + legBalanced(l).balanced, 0)),
    [excessLegs, products]);

  // ── Whole-batch balance meter — exactly the backend gate ───────────────────
  const batchPlaced = round(orderPortion + placedBalanced);   // make-up excluded
  const meterDiff = round(batchPlaced - totalMilled);
  const balanced = Math.abs(meterDiff) <= TOL;

  // ── Per-order projection (multi-batch) + close/make-up coverage ────────────
  const orderRows = allocations.map(a => {
    const projected = round(a.already_filled_lbs + a.allocated_weight);
    const remaining = round(a.order_item_total_lbs - projected);
    const mu = makeUps[a.order_item_id];
    const makeupLbs = mu ? round(mu.lbs || 0) : 0;
    const closing = closeIds.has(a.order_item_id);
    const shortUncovered = closing && round(projected + makeupLbs) < a.order_item_total_lbs - TOL;
    const makeupMissingPicker = closing && makeupLbs > 0
      && ((mu?.source_type === 'BYOC_BIN' && !mu?.bin_id)
        || (mu?.source_type === 'FEED_BANK' && !mu?.feed_bank_id));
    return { a, projected, remaining, makeupLbs, closing, shortUncovered, makeupMissingPicker };
  });

  const legMissingPicker = excessLegs.some(l =>
    (l.lbs || 0) > 0 && ((l.type === 'BAGGER' && !l.store_product_id) || (l.type === 'BYOC_BIN' && !l.bin_id)));
  const anyShortUncovered = orderRows.some(r => r.shortUncovered || r.makeupMissingPicker);

  const canSubmit = !saving && balanced && !legMissingPicker && !anyShortUncovered;

  // ── Mutators ───────────────────────────────────────────────────────────────
  const updateLeg = (i: number, patch: Partial<ExcessLeg>) =>
    setExcessLegs(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLeg = (i: number) => setExcessLegs(prev => prev.filter((_, idx) => idx !== i));
  const addLeg = () => {
    const remaining = round(excessTotal - excessLegs.reduce((s, l) => s + (l.lbs || 0), 0));
    setExcessLegs(prev => [...prev, { type: 'FEED_BANK', lbs: Math.max(0, Math.round(remaining)) }]);
  };
  const toggleClose = (oiId: number, remaining: number) => {
    setCloseIds(prev => {
      const next = new Set(prev);
      if (next.has(oiId)) {
        next.delete(oiId);
      } else {
        next.add(oiId);
        // Seed a make-up for the remaining if one isn't started.
        setMakeUps(m => m[oiId] ? m : {
          ...m, [oiId]: { order_item_id: oiId, lbs: Math.max(0, Math.round(remaining)), source_type: 'BYOC_BIN' },
        });
      }
      return next;
    });
  };
  const updateMakeUp = (oiId: number, patch: Partial<MakeUp>) =>
    setMakeUps(prev => ({ ...prev, [oiId]: { ...prev[oiId], ...patch } as MakeUp }));

  const submit = () => {
    const closeList = Array.from(closeIds);
    onSubmit({
      notes,
      excess_distribution: excessLegs.filter(l => (l.lbs || 0) > 0),
      close_order_items: closeList,
      make_up: closeList.map(id => makeUps[id]).filter(m => m && (m.lbs || 0) > 0),
    });
  };

  const meterColor = balanced ? '#155724' : '#721c24';
  const meterBg = balanced ? '#d4edda' : '#f8d7da';

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" style={{ maxWidth: 720, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Disposition Batch {batchNumber}</h3>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="modal-body">
          {/* 1. THE BATCH */}
          <div className="completion-summary" style={{ marginBottom: 12 }}>
            <p><strong>Formula:</strong> {formulaName}</p>
            <p><strong>Total milled:</strong> {totalMilled.toLocaleString()} lbs</p>
          </div>

          {/* 2. ORDER PORTION (locked) + close/make-up */}
          {allocations.length > 0 && (
            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 6 }}>
                Order portion (auto-filled): {orderPortion.toLocaleString()} lbs
              </label>
              {orderRows.map(({ a, projected, remaining, closing, shortUncovered, makeupMissingPicker }) => {
                const mu = makeUps[a.order_item_id];
                return (
                  <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <span><strong>#{a.order_number}</strong> — {a.customer_name}</span>
                      <span style={{ fontWeight: 600 }}>this batch → {a.allocated_weight.toLocaleString()} lbs</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      Line {a.order_item_total_lbs.toLocaleString()} lbs · already filled {a.already_filled_lbs.toLocaleString()} ·
                      {' '}after this batch {projected.toLocaleString()}
                      {remaining > TOL
                        ? <span style={{ color: '#b45309', fontWeight: 600 }}> · {remaining.toLocaleString()} lbs remaining (open)</span>
                        : <span style={{ color: '#155724', fontWeight: 600 }}> · complete ✓</span>}
                    </div>

                    {remaining > TOL && (
                      <div style={{ marginTop: 6 }}>
                        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="checkbox" checked={closing}
                                 onChange={() => toggleClose(a.order_item_id, remaining)} />
                          Close this order now (top off {remaining.toLocaleString()} lbs from a bin / feed bank)
                        </label>

                        {closing && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            <select className="form-select" style={{ flex: '1 1 130px' }}
                                    value={mu?.source_type || 'BYOC_BIN'}
                                    onChange={e => updateMakeUp(a.order_item_id, {
                                      source_type: e.target.value as MakeUp['source_type'], bin_id: undefined, feed_bank_id: undefined })}>
                              <option value="BYOC_BIN">From BYOC bin</option>
                              <option value="FEED_BANK">From feed bank</option>
                            </select>
                            {(mu?.source_type ?? 'BYOC_BIN') === 'BYOC_BIN' ? (
                              <select className="form-select" style={{ flex: '1 1 160px' }}
                                      value={mu?.bin_id || ''}
                                      onChange={e => updateMakeUp(a.order_item_id, { bin_id: Number(e.target.value) || undefined })}>
                                <option value="">-- Select bin --</option>
                                {bins.map(b => <option key={b.id} value={b.id}>{b.name} ({b.current_lbs} lbs)</option>)}
                              </select>
                            ) : (
                              <select className="form-select" style={{ flex: '1 1 160px' }}
                                      value={mu?.feed_bank_id || ''}
                                      onChange={e => updateMakeUp(a.order_item_id, { feed_bank_id: Number(e.target.value) || undefined })}>
                                <option value="">-- Select feed bank --</option>
                                {feedBank.map(f => <option key={f.id} value={f.id}>#{f.id} {f.feed_form} ({f.quantity_lbs} lbs)</option>)}
                              </select>
                            )}
                            <input type="number" className="form-input" style={{ width: 90, textAlign: 'right' }}
                                   value={mu?.lbs || ''} min={0}
                                   onChange={e => updateMakeUp(a.order_item_id, { lbs: Number(e.target.value) || 0 })} />
                            <span>lbs</span>
                          </div>
                        )}
                        {shortUncovered && (
                          <div style={{ color: '#721c24', fontSize: 12, marginTop: 4 }}>
                            Still short — declare a make-up that covers the remaining {remaining.toLocaleString()} lbs.
                          </div>
                        )}
                        {makeupMissingPicker && (
                          <div style={{ color: '#721c24', fontSize: 12, marginTop: 4 }}>
                            Select where the make-up comes from.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 3. EXCESS TO PLACE */}
          {excessTotal > 0 && (
            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 6 }}>
                Excess to place: {excessTotal.toLocaleString()} lbs
              </label>
              {excessLegs.map((leg, idx) => {
                const lb = legBalanced(leg);
                return (
                  <div key={idx} style={{ border: '1px solid #ddd', borderRadius: 6, padding: 10, marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select className="form-select" style={{ flex: '1 1 140px' }}
                              value={leg.type}
                              onChange={e => updateLeg(idx, { type: e.target.value as ExcessType, store_product_id: undefined, bin_id: undefined })}>
                        <option value="FEED_BANK">Feed Bank</option>
                        {products.length > 0 && <option value="BAGGER">Bagger</option>}
                        {bins.length > 0 && <option value="BYOC_BIN">BYOC Bin</option>}
                      </select>
                      <input type="number" className="form-input" style={{ width: 100, textAlign: 'right' }}
                             value={leg.lbs || ''} min={0}
                             onChange={e => updateLeg(idx, { lbs: Number(e.target.value) || 0 })} placeholder="lbs" />
                      <span>lbs</span>
                      {excessLegs.length > 1 && (
                        <button type="button" className="btn btn-sm"
                                style={{ color: '#dc3545', background: 'none', border: 'none', fontSize: 18 }}
                                onClick={() => removeLeg(idx)} title="Remove">&times;</button>
                      )}
                    </div>

                    {leg.type === 'BAGGER' && (
                      <div style={{ marginTop: 6 }}>
                        <select className="form-select" style={{ width: '100%' }}
                                value={leg.store_product_id || ''}
                                onChange={e => updateLeg(idx, { store_product_id: Number(e.target.value) || undefined })}>
                          <option value="">-- Select product --</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.size_lbs} lb {p.form})</option>)}
                        </select>
                        {leg.store_product_id && (leg.lbs || 0) > 0 && lb.bagCount !== undefined && (
                          <span style={{ fontSize: 13, color: lb.remainder && lb.remainder > 0 ? '#b45309' : '#666', display: 'block', marginTop: 4 }}>
                            = {lb.bagCount} bag{lb.bagCount !== 1 ? 's' : ''} ({lb.balanced} lbs)
                            {lb.remainder && lb.remainder > 0
                              ? ` + ${lb.remainder} lbs loose — add a bin/feed-bank leg for it`
                              : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {leg.type === 'BYOC_BIN' && (
                      <div style={{ marginTop: 6 }}>
                        <select className="form-select" style={{ width: '100%' }}
                                value={leg.bin_id || ''}
                                onChange={e => updateLeg(idx, { bin_id: Number(e.target.value) || undefined })}>
                          <option value="">-- Select bin --</option>
                          {bins.map(b => <option key={b.id} value={b.id}>{b.name} ({b.percentage_full}% full, {b.available_capacity_lbs} lbs avail)</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
              <button type="button" className="btn btn-sm btn-secondary" onClick={addLeg}>+ Add destination</button>
            </div>
          )}

          {/* 4. LIVE BALANCE METER */}
          <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 4, background: meterBg, color: meterColor, fontWeight: 600 }}>
            Placed {batchPlaced.toLocaleString()} / {totalMilled.toLocaleString()} lbs milled
            {balanced ? ' — balanced ✓'
              : meterDiff > 0 ? ` — over by ${Math.abs(meterDiff).toLocaleString()} lbs`
              : ` — ${Math.abs(meterDiff).toLocaleString()} lbs unplaced`}
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Completion notes (optional)</label>
            <textarea className="form-textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-success" onClick={submit} disabled={!canSubmit}>
            {saving ? 'Completing...' : 'Complete & Disposition'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchDispositionModal;
